export type ProjectStatus = "live" | "beta" | "building" | "dormant";

export interface Project {
  id: string;
  name: string;
  status: ProjectStatus;
  url?: string;
  blurb: string;
  tags: string[];
}

export const projects: Project[] = [
  {
    id: "mnemo",
    name: "Mnemo",
    status: "live",
    blurb: "The persistent memory layer behind agents like Adam and BP Agent — semantic, episodic, procedural memory. Postgres + pgvector. Not a standalone app; an engine embedded where it's needed.",
    tags: ["ai", "postgres", "pgvector", "memory", "infra"],
  },
  {
    id: "notetaker",
    name: "Notetaker",
    status: "live",
    url: "https://notetaker.brainsteam.cloud",
    blurb: "Joins meetings, transcribes, and surfaces follow-ups — auto-creating memories and GitHub issues from the transcript.",
    tags: ["ai", "meetings", "audio", "memory", "github"],
  },
  {
    id: "shipmate",
    name: "Shipmate",
    status: "live",
    url: "https://shipmate.brainsteam.cloud",
    blurb: "A voice abstraction layer over all my AI agents — talk to Claude Code sessions, send cross-session messages, open GitHub issues by voice.",
    tags: ["ai", "voice", "github", "claude-code", "messaging"],
  },
  {
    id: "relay",
    name: "Relay",
    status: "live",
    blurb: "Shared messaging service for the brainsteam estate — Telegram, WhatsApp, and SMS from one API.",
    tags: ["messaging", "infra", "ai"],
  },
  {
    id: "adam",
    name: "Adam",
    status: "live",
    blurb: "My personal agent experimentation hub — a proactive AI partner that watches over the rest of the estate and nudges me when it matters.",
    tags: ["ai", "claude-code", "personal", "infra"],
  },
  {
    id: "master-control",
    name: "Master Control",
    status: "building",
    blurb: "A Claude Code session that controls other Claude Code sessions. Mostly a concept piece — orchestration for swarms of agents.",
    tags: ["ai", "claude-code", "infra"],
  },
  {
    id: "agent-secrets",
    name: "Agent Secrets",
    status: "building",
    blurb: "Local encrypted vault for AI agents. Lets sessions resolve API keys without ever seeing plaintext on disk.",
    tags: ["infra", "claude-code", "security"],
  },
  {
    id: "local-launcher",
    name: "Local Launcher",
    status: "building",
    blurb: "One app to spin up and shut down the whole Docker estate. Less typing, less remembering which port is which.",
    tags: ["infra", "docker"],
  },
  {
    id: "dnd-dojo",
    name: "D&D Dojo",
    status: "beta",
    url: "https://dnd.brainsteam.cloud",
    blurb: "Solo D&D practice simulator with AI-generated party members, voice combat narration, and a tactical hex grid.",
    tags: ["ai", "voice", "game"],
  },
  {
    id: "forge",
    name: "Forge",
    status: "building",
    blurb: "A guided conversation tool: an AI voice agent interviews you about something you care about, then distils it into something useful.",
    tags: ["ai", "voice", "interview"],
  },
  {
    id: "artefact-editor",
    name: "Artefact Editor",
    status: "building",
    blurb: "A direct-manipulation editor for AI outputs — tweak, refine, and recompose the things agents produce instead of re-prompting them.",
    tags: ["ai", "editor"],
  },
  {
    id: "ads-manager",
    name: "Ads Manager",
    status: "building",
    blurb: "Multi-platform ad campaign manager for the brainsteam apps. Push creative and audiences out to Meta, X, LinkedIn, and others from one place.",
    tags: ["ads", "marketing", "saas"],
  },
  {
    id: "mini-momentum",
    name: "Mini Momentum",
    status: "live",
    url: "https://minimomentum.co.uk",
    blurb: "Holiday camp booking system used by netball clubs. React + Go + Postgres.",
    tags: ["postgres", "go", "saas", "sports"],
  },
  {
    id: "momentum-sports-technology",
    name: "Momentum Sports Tech",
    status: "live",
    blurb: "Backend platform powering the Momentum club's website, fixtures, results, and member ops.",
    tags: ["sports", "saas", "go"],
  },
  {
    id: "astonish",
    name: "Astonish Email",
    status: "live",
    url: "https://astonishemail.com",
    blurb: "Email marketing platform. Twelve years old this year and still kicking. Ruby on Rails.",
    tags: ["ruby", "email", "saas"],
  },
  {
    id: "mail-deliveri",
    name: "Mail Deliveri",
    status: "building",
    blurb: "Self-hosted email-sending service. Built to replace SendGrid for Astonish. Pre-deploy.",
    tags: ["email", "infra"],
  },
  {
    id: "innbox",
    name: "Innbox",
    status: "building",
    blurb: "My own email handler with unique data views of the inbox — making email less anxiety-inducing.",
    tags: ["go", "postgres", "email", "messaging"],
  },
  {
    id: "goalgrid",
    name: "GoalGrid",
    status: "building",
    url: "https://bighairyaudaciousgoal.com",
    blurb: "Goal tracking app. Vibe-coded in one evening, hooked to Stripe, awaiting marketing.",
    tags: ["saas", "stripe"],
  },
  {
    id: "health",
    name: "Health",
    status: "live",
    url: "https://bigadamknight.brainsteam.cloud",
    blurb: "My personal health dashboard — Thriva blood tests, CGM, body composition, and habits in one view.",
    tags: ["personal", "data"],
  },
  {
    id: "help-people-help",
    name: "Help People Help",
    status: "building",
    blurb: "A concept: put tools in the hands of people to distil their knowledge and share it with others at scale.",
    tags: ["ai", "concept"],
  },
  {
    id: "findingratpark",
    name: "Finding Rat Park",
    status: "live",
    url: "https://findingratpark.co.uk",
    blurb: "Part of the Help People Help thread — a body of work on what really drives addiction, told as an interactive site.",
    tags: ["concept", "content"],
  },
  {
    id: "knowledge-tree",
    name: "Knowledge Tree",
    status: "building",
    blurb: "A double-sided educational platform — learners traverse a Civilization-style hex tree of skills, mentors plant the nodes.",
    tags: ["education", "builder"],
  },
  {
    id: "portal",
    name: "Portal",
    status: "beta",
    blurb: "Once-a-day news summary of what I actually want to see — X, Reddit, Google News distilled, with none of the filler.",
    tags: ["ai", "content"],
  },
  {
    id: "see-you-in-the-morning",
    name: "See You In The Morning",
    status: "building",
    blurb: "A children's book project — generate, illustrate, and store bedtime stories using AI tools.",
    tags: ["ai", "creative", "content"],
  },
  {
    id: "brainsteam-blog",
    name: "BrainSteam Blog",
    status: "live",
    url: "https://blog.brainsteam.cloud",
    blurb: "Minimalist multi-tenant blogging platform. Go API, React frontend, the home of brainsteam writing.",
    tags: ["go", "saas", "content"],
  },
  {
    id: "brainsteam-builder",
    name: "BrainSteam Builder",
    status: "live",
    blurb: "AI-driven website builder for sports teams — themes, fixtures, news, sponsors, on a custom domain.",
    tags: ["builder", "sports", "saas"],
  },
  {
    id: "brainsteam-labs",
    name: "BrainSteam Labs",
    status: "live",
    url: "https://brainsteam.cloud",
    blurb: "You're looking at it. The constellation of everything I'm building, with a terminal to poke around.",
    tags: ["labs", "self"],
  },
  {
    id: "3d-gallery",
    name: "3D Gallery",
    status: "building",
    blurb: "A small experiment in 3D scene composition for the web — Docker stack, Postgres, room for more.",
    tags: ["3d", "creative"],
  },
  {
    id: "nocode",
    name: "NoCode",
    status: "dormant",
    blurb: "Build a website from a Google Doc. Got Product of the Day on Product Hunt, then went quiet.",
    tags: ["builder", "google-docs"],
  },
];

export interface Edge {
  source: string;
  target: string;
  weight: number;
}

export function deriveEdges(projects: Project[]): Edge[] {
  const edges: Edge[] = [];
  for (let i = 0; i < projects.length; i++) {
    for (let j = i + 1; j < projects.length; j++) {
      const a = projects[i];
      const b = projects[j];
      const shared = a.tags.filter((t) => b.tags.includes(t));
      if (shared.length > 0) {
        edges.push({ source: a.id, target: b.id, weight: shared.length });
      }
    }
  }
  return edges;
}
